import { Helmet } from "react-helmet-async";

/**
 * <SEO />
 * --------
 * Centralised, enterprise-grade SEO injection for PrimeS4 AI marketing
 * pages. Renders page-specific `<title>`, meta description / keywords,
 * Open Graph + Twitter cards, canonical URL, and optional JSON-LD
 * structured data — all via react-helmet-async so the values are picked
 * up by crawlers, AI search agents, and LinkedIn / X share unfurlers.
 *
 * Usage:
 *   <SEO
 *     title="PRIVA | Operational Intelligence for Enterprise Execution"
 *     description="…"
 *     canonical="/priva"
 *     keywords={["SAP AI", "Operational Intelligence", …]}
 *     ogImage="https://…/priva-og.png"
 *   />
 *
 * Notes:
 *   - `canonical` accepts either an absolute URL or a path; the path is
 *     resolved against CANONICAL_BASE.
 *   - `image` defaults to the global PrimeS4 wordmark so every page
 *     unfurls with brand-aligned imagery.
 *   - All keyword usage is moderate and natural — no stuffing.
 */
const CANONICAL_BASE = "https://www.primes4.com";

// Served from our own domain (bundled in /public) — kept absolute for social crawlers.
const DEFAULT_IMAGE = `${CANONICAL_BASE}/images/primes4-wordmark-gradient-3-0.webp`;

export default function SEO({
  title,
  description,
  keywords = [],
  canonical,
  image,
  imageAlt,
  noindex = false,
  schema, // optional JSON-LD object or array
  type = "website",
}) {
  const url = canonical
    ? canonical.startsWith("http")
      ? canonical
      : `${CANONICAL_BASE}${canonical.startsWith("/") ? "" : "/"}${canonical}`
    : CANONICAL_BASE;
  const ogImage = image || DEFAULT_IMAGE;
  const kw = Array.isArray(keywords) ? keywords.join(", ") : keywords || "";

  return (
    <Helmet prioritizeSeoTags>
      <title>{title}</title>
      <meta name="description" content={description} />
      {kw ? <meta name="keywords" content={kw} /> : null}
      <link rel="canonical" href={url} />
      {noindex ? <meta name="robots" content="noindex,nofollow" /> : null}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="PrimeS4 AI" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:alt" content={imageAlt || title} />

      {/* Twitter / X */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      {imageAlt ? <meta name="twitter:image:alt" content={imageAlt} /> : null}

      {/* Structured data (JSON-LD) */}
      {schema ? (
        <script type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ) : null}
    </Helmet>
  );
}

// Convenience: brand-wide Organization schema used by the home page.
export const ORGANIZATION_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "PrimeS4 AI",
  alternateName: ["PrimeS4", "PrimeS4 AI Ecosystem"],
  url: CANONICAL_BASE,
  logo: DEFAULT_IMAGE,
  description:
    "PrimeS4 AI delivers operational intelligence, SAP-aligned AI platforms, warehouse transformation, and enterprise execution innovation powered by PRIVA and WarehouseX.",
  knowsAbout: [
    "SAP S/4HANA",
    "SAP EWM",
    "SAP BTP",
    "Enterprise AI",
    "Operational Intelligence",
    "Supply Chain Transformation",
    "Warehouse Automation",
    "Intelligent Enterprise Operations",
  ],
  sameAs: ["https://www.linkedin.com/company/primes4-ai"],
};

export const WEBSITE_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "PrimeS4 AI",
  url: CANONICAL_BASE,
  publisher: { "@type": "Organization", name: "PrimeS4 AI" },
};
